"use client";

import { useRef } from "react";
import { Popover } from "@/components/ui/atoms/popover";
import type { Tonality } from "@/lib/music/tonality";
import { TonalityPickerChip } from "./tonality-picker-chip";
import { TonalityPickerFreePanel } from "./tonality-picker-free-panel";
import { TonalityPickerTransposePanel } from "./tonality-picker-transpose-panel";
import { useTonalityPicker } from "./use-tonality-picker";

interface TonalityPickerProps {
  /** Current global tonality (from Studio). */
  tonality: Tonality | null;
  /** Source tonality detected on the loaded piece, if any. */
  sourceTonality?: Tonality | null;
  /** Pick a tonality (or clear it with null) when no piece is loaded. */
  onPick: (t: Tonality | null) => void;
  /** Apply a transposition to the loaded piece. */
  onTranspose?: (target: Tonality) => void | Promise<void>;
  /**
   * Classes ajoutées à l'enveloppe du chip, pour qu'un appelant puisse le
   * retirer en CSS sous une requête média. Le panneau, lui, n'est monté que
   * lorsqu'il est ouvert, et un chip caché ne s'ouvre pas.
   */
  className?: string;
}

export function TonalityPicker({
  tonality,
  sourceTonality,
  onPick,
  onTranspose,
  className = "",
}: TonalityPickerProps) {
  // Le ref d'ancrage reste ici plutôt que dans le hook : c'est une affaire de
  // vue, pas d'état du sélecteur.
  const anchorRef = useRef<HTMLDivElement>(null);
  const picker = useTonalityPicker({
    tonality,
    sourceTonality,
    onPick,
    onTranspose,
  });

  return (
    <>
      {/* Le ref se pose sur l'enveloppe, pas sur le chip : passé en prop à un
          composant, il compterait comme une lecture de ref pendant le rendu.
          L'enveloppe épouse exactement le bouton. */}
      <div ref={anchorRef} className={`flex shrink-0 ${className}`}>
        <TonalityPickerChip
          tonality={tonality}
          appliedSemis={picker.appliedSemis}
          sourceTonality={sourceTonality ?? null}
          applyState={picker.applyState}
          open={picker.open}
          onClick={picker.toggleOpen}
        />
      </div>

      {/* Le panneau était positionné en absolu sur le chip, sans jamais se
          replier : à 390 px il s'étendait jusqu'à x≈500, la colonne « Mineur »
          hors écran et donc aucune tonalité mineure choisissable. Le primitif
          `Popover` le ramène dans le cadre et le suit au redimensionnement.

          Deux colonnes conservées même en compact : le studio ne s'affiche
          plus en portrait, donc la largeur minimale servie est 667 px, où
          460 px laissent deux colonnes confortables. C'est la hauteur qui
          manque en paysage — empiler majeurs puis mineurs doublerait la
          longueur de la liste. */}
      <Popover
        anchorRef={anchorRef}
        open={picker.open}
        onClose={picker.closePopover}
        placement="bottom-start"
        className="w-[min(460px,calc(100vw-2rem))] max-h-[calc(100dvh-8rem)] overflow-y-auto p-[18px]"
      >
        {picker.hasPiece && sourceTonality ? (
          <TonalityPickerTransposePanel
            source={sourceTonality}
            applied={tonality}
            appliedSemis={picker.appliedSemis}
            staged={picker.stagedTarget}
            stagedSemis={picker.stagedSemis}
            dirty={picker.dirty}
            applyState={picker.applyState}
            onStep={picker.step}
            onSetSemis={picker.setStagedSemis}
            onResetStaged={picker.resetStaged}
            onApply={picker.apply}
          />
        ) : (
          <TonalityPickerFreePanel
            tonality={tonality}
            onPick={picker.pickFree}
            onClear={picker.clearTonality}
          />
        )}
      </Popover>
    </>
  );
}
